/**
 * One bulk send: its name and email, its sending limit and warm-up, who it
 * goes to and when, and how it did. `/emails/bulk/new` asks for the name and
 * email first, then makes it (a switched-on, send-once automation marked
 * `bulk_send`: a segment trigger and a Send email step) and opens it here.
 *
 * Who and when is the automation builder's own segment card
 * (`SendToSegment`), so the preview, the address check, scheduling and
 * cancelling behave exactly as they do there. Once it has started sending,
 * the email can no longer be changed; the limit can.
 */
import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  ActionIcon, Alert, Anchor, Badge, Button, Card, Group, Loader, Menu, Select, Stack, Text, TextInput, Title,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconArrowLeft, IconCopy, IconDots, IconExternalLink } from '@tabler/icons-react';
import { useEmailBrands, useEmailTemplates } from '../../api/queries';
import {
  useWorkflow, useWorkflowEdits, useWorkflows, type CreateWorkflowBody, type WorkflowStep,
} from '../../api/workflows';
import { parseRamp, showRamp } from './warmup';
import { MAX_DAILY_LIMIT, SendingCard } from '../workflows/SendingCard';
import { SendToSegment } from '../workflows/SendToSegment';
import { EmailSection } from '../workflows/AnalyticsPanel';
import { bulkStatus } from './BulkSendsTab';

const SEND_ID = 'send';

function todayName(): string {
  const d = new Date();
  return `Bulk send, ${d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}`;
}

/** The new bulk send's steps: the email, then done. */
function bulkSteps(templateId: string, brandId: string | null): WorkflowStep[] {
  return [
    { id: SEND_ID, kind: 'send_email', config: { template_id: templateId, brand_id: brandId ?? undefined, once_per_person: true } },
    { id: 'finish', kind: 'end', config: { outcome: 'done' } },
  ] as WorkflowStep[];
}

function EmailPicker({
  templateId, brandId, onTemplate, onBrand, disabled,
}: {
  templateId: string | null; brandId: string | null;
  onTemplate: (id: string | null) => void; onBrand: (id: string | null) => void; disabled?: boolean;
}) {
  const templates = useEmailTemplates().data?.templates ?? [];
  const brands = useEmailBrands().data?.brands ?? [];
  return (
    <Stack gap="xs">
      <Group grow align="flex-start">
        <Select
          label="Email"
          placeholder="Choose a template"
          searchable
          data={templates.map((t) => ({ value: t.id, label: t.name }))}
          value={templateId}
          onChange={onTemplate}
          disabled={disabled}
          data-testid="bulk-template"
        />
        <Select
          label="Brand"
          placeholder="The default brand"
          clearable
          data={brands.map((b) => ({ value: b.id, label: b.name }))}
          value={brandId}
          onChange={onBrand}
          disabled={disabled}
          data-testid="bulk-brand"
        />
      </Group>
      <Group gap="md">
        {templateId && (
          <Anchor component={Link} to={`/emails/${templateId}`} size="xs">Edit this email</Anchor>
        )}
        <Anchor component={Link} to="/emails?tab=templates" size="xs">Make a new email</Anchor>
      </Group>
    </Stack>
  );
}

export function BulkSendPage() {
  const { id } = useParams();
  return id === 'new' ? <NewBulkSend /> : <BulkSend id={id!} />;
}

function Back() {
  return (
    <Anchor component={Link} to="/emails?tab=bulk" size="sm">
      <Group gap={4}><IconArrowLeft size={14} />Bulk sends</Group>
    </Anchor>
  );
}

function NewBulkSend() {
  const navigate = useNavigate();
  const edits = useWorkflowEdits();
  const brands = useEmailBrands().data?.brands;
  const [name, setName] = useState(todayName);
  const [templateId, setTemplateId] = useState<string | null>(null);
  const [brandId, setBrandId] = useState<string | null>(null);
  // With one brand, it is the brand.
  useEffect(() => {
    if (!brandId && brands?.length === 1) setBrandId(brands[0]!.id);
  }, [brands, brandId]);

  async function make() {
    if (!templateId) return;
    try {
      const body: CreateWorkflowBody = {
        name: name.trim() || todayName(),
        trigger: { kind: 'enrolment', record_type: 'submission' },
        conditions: [],
        steps: bulkSteps(templateId, brandId),
        enabled: true,
        bulk_send: true,
      };
      const w = await edits.create.mutateAsync(body);
      navigate(`/emails/bulk/${w.id}`, { replace: true });
    } catch (e) {
      notifications.show({ color: 'red', title: 'Could not make the bulk send', message: (e as Error).message });
    }
  }

  return (
    <Stack gap="md" maw={760}>
      <Back />
      <Title order={2}>New bulk send</Title>
      <Card withBorder padding="md">
        <Stack gap="sm">
          <TextInput label="Name" description="Only you see this." value={name} onChange={(e) => setName(e.currentTarget.value)} data-testid="bulk-name" />
          <EmailPicker templateId={templateId} brandId={brandId} onTemplate={setTemplateId} onBrand={setBrandId} />
          <Group justify="flex-end">
            <Button onClick={() => void make()} disabled={!templateId} loading={edits.create.isPending} data-testid="bulk-continue">
              Next: who it goes to
            </Button>
          </Group>
        </Stack>
      </Card>
    </Stack>
  );
}

function BulkSend({ id }: { id: string }) {
  const navigate = useNavigate();
  const q = useWorkflow(id);
  const list = useWorkflows();
  const edits = useWorkflowEdits();
  const w = q.data;
  const summary = list.data?.workflows.find((s) => s.id === id);
  const canManage = !!list.data?.can_manage;
  const send = useMemo(() => w?.steps.find((s) => s.kind === 'send_email'), [w]);
  const cfg = (send?.config ?? {}) as { template_id?: string; brand_id?: string };

  const [name, setName] = useState('');
  const [templateId, setTemplateId] = useState<string | null>(null);
  const [brandId, setBrandId] = useState<string | null>(null);
  const [dailyLimit, setDailyLimit] = useState(0);
  const [warmup, setWarmup] = useState('');
  const [loaded, setLoaded] = useState<string | null>(null);
  useEffect(() => {
    if (!w || loaded === `${w.id}:${w.updated_at}`) return;
    setName(w.name);
    setTemplateId(cfg.template_id || null);
    setBrandId(cfg.brand_id || null);
    setDailyLimit(w.send_limit?.daily ?? 0);
    setWarmup(showRamp(w.send_limit?.warmup));
    setLoaded(`${w.id}:${w.updated_at}`);
  }, [w, cfg.template_id, cfg.brand_id, loaded]);

  if (q.error) return <Alert color="red" variant="light">{q.error.message}</Alert>;
  if (!w) return <Group justify="center" py="xl"><Loader size="sm" /></Group>;
  if (!w.bulk_send) {
    return (
      <Stack gap="sm">
        <Back />
        <Alert variant="light">
          This is an automation, not a bulk send. <Anchor component={Link} to={`/automations/${w.id}`}>Open it in Automations</Anchor>.
        </Alert>
      </Stack>
    );
  }

  const st = bulkStatus({ send_once: summary?.send_once ?? null, archived: !!w.archived_at });
  const started = summary?.send_once?.state === 'sending' || summary?.send_once?.state === 'sent';
  const ramp = parseRamp(warmup);
  const emailDirty = (templateId ?? '') !== (cfg.template_id ?? '') || (brandId ?? '') !== (cfg.brand_id ?? '');
  const limitDirty = dailyLimit !== (w.send_limit?.daily ?? 0) || showRamp(ramp) !== showRamp(w.send_limit?.warmup);
  const dirty = name.trim() !== w.name || emailDirty || limitDirty;

  async function save() {
    if (!w) return;
    if (ramp.some((n) => n > MAX_DAILY_LIMIT)) {
      notifications.show({ color: 'red', title: 'Not saved', message: `Each warm-up day can be at most ${MAX_DAILY_LIMIT.toLocaleString('en-GB')} emails.` });
      return;
    }
    if (!templateId) {
      notifications.show({ color: 'red', title: 'Not saved', message: 'Choose the email to send.' });
      return;
    }
    try {
      await edits.update.mutateAsync({
        id: w.id,
        body: {
          name: name.trim() || w.name,
          ...(emailDirty ? {
            steps: w.steps.map((s) => (s.id === send?.id
              ? { ...s, config: { ...(s.config as Record<string, unknown>), template_id: templateId, brand_id: brandId ?? undefined } }
              : s)),
          } : {}),
          send_limit: { daily: dailyLimit, warmup: ramp },
        },
      });
      notifications.show({ color: 'teal', message: 'Saved' });
    } catch (e) {
      notifications.show({ color: 'red', title: 'Not saved', message: (e as Error).message });
    }
  }

  async function duplicate() {
    try {
      const copy = await edits.duplicate.mutateAsync(id);
      // A copy starts switched off; a bulk send has to be on to be scheduled.
      await edits.setEnabled.mutateAsync({ id: copy.id, enabled: true });
      navigate(`/emails/bulk/${copy.id}`);
    } catch (e) {
      notifications.show({ color: 'red', title: 'Could not duplicate', message: (e as Error).message });
    }
  }

  return (
    <Stack gap="md" maw={980} data-testid="bulk-send-page">
      <Back />
      <Group justify="space-between" align="flex-start">
        <div>
          <Title order={2}>{w.name}</Title>
          <Group gap="xs" mt={4}>
            <Badge variant="light" color={st.color} data-testid="bulk-status">{st.label}</Badge>
            {st.detail && <Text size="sm" c="dimmed">{st.detail}</Text>}
          </Group>
        </div>
        <Group gap="xs">
          {canManage && dirty && (
            <Button onClick={() => void save()} loading={edits.update.isPending} data-testid="bulk-save">Save changes</Button>
          )}
          <Menu position="bottom-end" withinPortal>
            <Menu.Target>
              <ActionIcon variant="default" size="lg" aria-label="More"><IconDots size={16} /></ActionIcon>
            </Menu.Target>
            <Menu.Dropdown>
              {canManage && (
                <Menu.Item leftSection={<IconCopy size={14} />} onClick={() => void duplicate()} data-testid="bulk-duplicate">
                  Duplicate, to send again
                </Menu.Item>
              )}
              <Menu.Item leftSection={<IconExternalLink size={14} />} component={Link} to={`/automations/${w.id}`}>
                Open as an automation
              </Menu.Item>
            </Menu.Dropdown>
          </Menu>
        </Group>
      </Group>

      <Card withBorder padding="md">
        <Stack gap="sm">
          <Text fw={600} size="sm">1. Name and email</Text>
          <TextInput label="Name" value={name} onChange={(e) => setName(e.currentTarget.value)} disabled={!canManage} />
          <EmailPicker
            templateId={templateId}
            brandId={brandId}
            onTemplate={setTemplateId}
            onBrand={setBrandId}
            disabled={!canManage || started}
          />
          {started && (
            <Text size="xs" c="dimmed">It has started sending, so the email stays as it is. Duplicate it to send a different one.</Text>
          )}
        </Stack>
      </Card>

      <Stack gap={4}>
        <Text fw={600} size="sm">2. How fast</Text>
        <SendingCard dailyLimit={dailyLimit} onChange={setDailyLimit} warmup={warmup} onWarmup={setWarmup} />
      </Stack>

      <Card withBorder padding="md">
        <Stack gap="sm">
          <Text fw={600} size="sm">3. Who and when</Text>
          {dirty && (
            <Text size="xs" c="orange">Save your changes before scheduling.</Text>
          )}
          <SendToSegment
            workflowId={w.id}
            enabled={w.enabled}
            sendsEmail
            recordType="submission"
            dirty={dirty}
            canManage={canManage}
            oneOff
          />
        </Stack>
      </Card>

      {summary?.send_once && (
        <Stack gap={4}>
          <Text fw={600} size="sm">4. How it did</Text>
          <EmailSection workflowId={w.id} steps={w.steps} />
        </Stack>
      )}
    </Stack>
  );
}
